import api from '../../lib/api';
import {
  AssessmentChangeLog,
  Survey,
  SurveyPayload,
  SurveyResponse,
  SurveyFilters,
  SurveyStatisticsData,
} from './types';
import type { SeriesStatistics } from './types';

export const fetchSurveys = async (params?: SurveyFilters): Promise<SurveyResponse> => {
  const { page = 1, pageSize = 20, search = '', category, type, active, sortBy = 'createdAt', sortDir = 'desc' } = params || {};
  const queryParams: Record<string, any> = { page, pageSize, sortBy, sortDir };

  if (search) queryParams.search = search;
  if (category) queryParams.category = category;
  if (type) queryParams.type = type;
  if (active !== undefined) queryParams.active = active;

  const { data } = await api.get<{ success: boolean; data: Survey[]; pagination: SurveyResponse['pagination'] }>(
    'assessments',
    { params: queryParams }
  );
  
  return {
    data: data.data,
    pagination: data.pagination,
  };
};

export const getSurveyById = async (id: string): Promise<Survey> => {
  const { data } = await api.get<{ success: boolean; data: Survey }>(`assessments/${id}`);
  return data.data;
};

export const createSurvey = async (payload: SurveyPayload): Promise<Survey> => {
  try {
    const { data } = await api.post<{ success: boolean; message: string; data: Survey }>('assessments', {
      ...payload,
      category: payload.category || 'survey',
      periodType: payload.type === 'periodic' ? payload.periodType : null,
    });
    return data.data;
  } catch (error: any) {
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error?.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const updateSurvey = async (id: string, payload: Partial<SurveyPayload & { active?: boolean }>): Promise<Survey> => {
  try {
    const { data } = await api.put<{ success: boolean; message: string; data: Survey }>(`assessments/${id}`, {
      ...payload,
      periodType: payload.type === 'periodic' ? payload.periodType : null
    });
    return data.data;
  } catch (error: any) {
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error?.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const deleteSurvey = async (id: string): Promise<void> => {
  await api.delete(`assessments/${id}`);
};

export interface GenerateTokenResponse {
  token: string;
  tokenId: string;
  expiresInDays: number | null;
}

export const generateSurveyToken = async (
  surveyId: string,
  expiresInDays?: number
): Promise<GenerateTokenResponse> => {
  try {
    const { data } = await api.post<{
      success: boolean;
      message: string;
      data: GenerateTokenResponse;
    }>(`assessments/${surveyId}/tokens`, { expiresInDays });
    return data.data;
  } catch (error: any) {
    if (error?.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    if (error?.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
};

export const getSurveyStatistics = async (
  surveyId: string,
  years: number = 5
): Promise<SurveyStatisticsData> => {
  const { data } = await api.get<{ success: boolean; data: SurveyStatisticsData }>(
    `assessments/${surveyId}/statistics`,
    { params: { years } }
  );
  return data.data;
};

export const getSeriesStatistics = async (seriesId: string): Promise<SeriesStatistics> => {
  const bounded = seriesId.slice(0, 120);
  const { data } = await api.get<{ success: boolean; data: SeriesStatistics }>(
    `assessments/series/${encodeURIComponent(bounded)}/statistics`,
  );
  return data.data;
};

export const getAssessmentChangeLogs = async (surveyId: string): Promise<AssessmentChangeLog[]> => {
  const { data } = await api.get<{ success: boolean; data: AssessmentChangeLog[] }>(
    `assessments/${surveyId}/change-logs`
  );
  return data.data;
};

export interface TeableSheetInfoLinked {
  linked: true;
  surveyId: string;
  title: string;
  baseId: string;
  tableId: string;
  embedUrl: string;
  canEdit: boolean;
}

export interface TeableSheetInfoUnlinked {
  linked: false;
  code: string;
  message: string;
  surveyId: string;
  title: string;
}

export type TeableSheetInfo = TeableSheetInfoLinked | TeableSheetInfoUnlinked;

export const getTeableSheetInfo = async (surveyId: string): Promise<TeableSheetInfo> => {
  const { data } = await api.get<{ success: boolean; data: TeableSheetInfo }>(
    `assessments/${surveyId}/teable-sheet`
  );
  return data.data;
};

export const saveWorkbookEmbedSnapshot = async (
  assessmentId: string,
  snapshot: Record<string, unknown>,
): Promise<void> => {
  await api.put(`assessments/${assessmentId}/workbook-embed/snapshot`, { snapshot });
};


